import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      // Collapsed sections are kept mounted but hidden, so they must not be
      // reachable by Tab.
      !element.closest('[aria-hidden="true"]') &&
      (element.offsetWidth > 0 ||
        element.offsetHeight > 0 ||
        element === document.activeElement)
  );
}

/**
 * Keeps Tab navigation inside `containerRef` while `active`, and restores focus
 * to the element that was focused before the trap mounted.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  active = true
): void {
  const hasFocusedRef = useRef(false);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Captured once, on mount, and restored once, on unmount. Reading it inside
  // the `active` effect would overwrite it with an element from inside this
  // modal every time a nested one closes, so the original opener would never
  // get focus back.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    // Only claim focus the first time the trap engages. Re-activating — which
    // happens when a nested modal closes and this one becomes topmost again —
    // must leave focus where the user left it instead of yanking it back to the
    // first control.
    if (!hasFocusedRef.current) {
      hasFocusedRef.current = true;

      const [firstFocusable] = getFocusableElements(container);
      (firstFocusable ?? container).focus?.();
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const { activeElement } = document;

      if (!container.contains(activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, containerRef]);
}
